import RemoveDialog from "@/components/RemoveDialog/RemoveDialog"
import { SESSION_DIALOG_BLURB, SESSION_DIALOG_TITLE, roomWillStop } from "@/content/archive"
import { useSessionParticipants } from "@/hooks/useSessionParticipants"
import { isJoinable } from "@/utils/sessionLifecycle"
import type { RemovalKind, Session } from "@/types"

/**
 * The confirmation in front of removing ONE session, from a list.
 *
 * Its own component for one reason: it counts the room, and the roster lives in
 * a subcollection, so it needs a hook that must not run until the dialog is
 * open. Rendered from a screen's body it would read every listed session's
 * participants on every render.
 *
 * The count is the whole point. "This session is live" is abstract; "4 people
 * are in this session right now" is the sentence that stops someone emptying a
 * room they meant to leave alone.
 *
 * Shared by the customer screen and the session index, which offer the same two
 * controls over the same document — a second copy would be the one that drifts.
 * `SessionSettings` does NOT use it: inside the room the participant count is
 * already in the store, and the warning is unconditional there — the settings
 * panel is unreachable on the two states this gate exists to suppress, because
 * `RoundControls` hides its button once a session is completed and is not
 * rendered at all once one has expired. If that ever stops being true, the gate
 * has to move with it.
 */
export default function SessionRemoveDialog({
  session,
  kind,
  error,
  pending,
  onClose,
  onConfirm,
}: {
  session: Session
  kind: RemovalKind
  error: string | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const participants = useSessionParticipants(session.id)
  // Not `live || paused`: a session nobody has started yet still has a working
  // link and can already have a lobby full of people. What the line is about is
  // whether removing it takes something away, which is exactly joinability.
  const admitsPeople = isJoinable(session)

  return (
    <RemoveDialog
      kind={kind}
      title={SESSION_DIALOG_TITLE[kind]}
      description={SESSION_DIALOG_BLURB[kind]}
      /* Only while the link still admits people. On a finished or expired
         session the warning would be noise, and a dialog that cries wolf on
         every session is one nobody reads on the session that matters. */
      detail={admitsPeople ? roomWillStop(participants.length) : undefined}
      error={error}
      pending={pending}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  )
}
